import React, { useCallback } from 'react';
import { MdDarkMode, MdLightMode } from 'react-icons/md';
import { IconButton, TopBar } from '@aibuild/ui';
import { useTheme } from '@contexts/useTheme';
import { usePageHelpTextValue, usePageTitleValue } from '@contexts/usePageTitle';
import FeedbackWidget from '@components/FeedbackWidget/FeedbackWidget';
import UserProfile from '@components/UserProfile';
import MobileMenuButton from '@components/MobileMenuButton';

// Reuses the same `TopBar` shell as the Processes pages so the avatar sits in
// an identical spot on every route (fixes the per-page vertical drift). The
// active page declares its title via `usePageTitle`; we render it on the left
// so every page's title lives in the navbar rather than the page body.
const AppTopBar: React.FC = () => {
  const { themeName, setThemePreference } = useTheme();
  const isDark = themeName === 'dark';
  const title = usePageTitleValue();
  const helpText = usePageHelpTextValue();

  const handleToggleTheme = useCallback(() => {
    setThemePreference(isDark ? 'light' : 'dark');
  }, [isDark, setThemePreference]);

  return (
    <TopBar
      dataTestId="app-top-bar"
      title={title}
      helpText={helpText}
      leading={<MobileMenuButton />}
      actions={
        <>
          <FeedbackWidget mode="inline" dataTestId="app-top-bar__feedback" />
          <IconButton
            ariaLabel={
              isDark ? 'Switch to light theme' : 'Switch to dark theme'
            }
            icon={isDark ? <MdLightMode size={20} /> : <MdDarkMode size={20} />}
            onClick={handleToggleTheme}
            dataTestId="app-top-bar__theme-toggle"
          />
          <UserProfile />
        </>
      }
    />
  );
};

export default AppTopBar;
